"use client";
import { Button } from "@/components/ui/button";
import { useCarouselAPIContext } from "@/contexts/CarouselAPIContext";
import { useCustomImageContext } from "@/contexts/CustomImageContext";
import { useGeneratedImages } from "@/contexts/GeneratedImagesProvider";
import { useNonUrlContext } from "@/contexts/NonUrlContext";
import { useToast } from "@/hooks/use-toast";
import { ColorType, ProductDetail } from "@/utils/APIRouteTypes";
import { cn } from "@/lib/utils";
import { getImageInitialProps } from "@/utils/helper";
import { Properties } from "@/utils/properties";
import { CheckCircle, Download } from "lucide-react";
import Link from "next/link";
import React, { useEffect, useMemo } from "react";
import { InstructionModal } from "../detailComponents/instructionModal";
import ImageUploader from "../ImageUpload/ImageUploader";
import ImageViewModal from "./generatedImageViewModal";
import { ImageGenerateItem } from "./imageGenerateItem";

type ImageGeneratorProps = {
  productDetails: ProductDetail
  uploadedImage?: boolean
  downloadEnabled?: boolean
};

export default function ImageGenerator(props: ImageGeneratorProps) {
  const { toast } = useToast()
  const [imagesContext, setImagesContext] = useCustomImageContext();
  const [openModal, setOpenModal] = React.useState(false);
  const [modalImage, setModalImage] = React.useState<{ src: string }>({ src: "" });
  const nonURIContext = useNonUrlContext()
  const [generatedImages, _] = useGeneratedImages()
  const [carouselAPI, setCarouselAPI] = useCarouselAPIContext()
  useEffect(() => {
    const images = [...imagesContext.frontImages, ...imagesContext.backImages]
    const imagesFromGenerated = generatedImages.filter(x => images.some(y => y.src === x.src))
    const imgTypes = new Set(imagesFromGenerated.map(x => x.color))
    const darkAvailable = imgTypes.has(ColorType.DARK)
    const lightAvailable = imgTypes.has(ColorType.LIGHT)
    if (darkAvailable && nonURIContext.colorMode === ColorType.LIGHT) {
      if (imagesContext.frontImages.length + imagesContext.backImages.length > 1)
        toast({
          description: `Some of your design(s) were generated for dark colored clothes, they may look weird on lighter colors.`
        })
      else if (imagesContext.frontImages.length + imagesContext.backImages.length === 1)
        toast({
          description: `Your design was generated for dark colored clothes, it may look weird on light colors.`
        })
    }
    if (lightAvailable && nonURIContext.colorMode === ColorType.DARK) {
      if (imagesContext.frontImages.length + imagesContext.backImages.length > 1)
        toast({
          description: "Some of your design(s) were generated for light colored clothes, they may look weird on darker colors."
        })
      else if (imagesContext.frontImages.length + imagesContext.backImages.length === 1)
        toast({
          description: "Your design was generated for light colored clothes, it may look weird on dark colors."
        })
    }
  }, [generatedImages.length, imagesContext.frontImages.length, imagesContext.backImages.length, nonURIContext.colorMode])

  const onReset = () => {
    setImagesContext({
      ...imagesContext,
      frontImages: [],
      backImages: [],
    });
  }

  const selectedImages = useMemo(
    () => [
      ...imagesContext.frontImages
        .map((image) => image.src),
      ...imagesContext.backImages
        .map((image) => image.src),
    ],
    [
      imagesContext.backImages.length,
      imagesContext.frontImages.length,
    ],
  );

  const handleAddToFrontImage = () => {
    if (!selectedImages.includes(modalImage.src)) {

      if (imagesContext.frontImages.length < 4) {
        const imgProps = getImageInitialProps(props.productDetails)

        if (carouselAPI?.selectedScrollSnap() !== 0)
          carouselAPI?.scrollTo(0)

        setImagesContext((prev) => {
          return {
            ...prev,
            frontImages: [
              ...prev.frontImages,
              {
                src: modalImage.src,
                ...imgProps
              },
            ],
          };
        });

      } else {
        toast({
          description: "You can add only 4 images.",
          variant: 'destructive',
          duration: 4000
        });
      }
    }
    if (window.innerWidth < 500)
      window.scrollTo(0, 0);
    setOpenModal(false);
  };

  const handleAddToBackImage = () => {

    if (props.productDetails.boundingBoxBack === null)
      return

    if (!selectedImages.includes(modalImage.src)) {
      if (imagesContext.backImages.length < 4) {
        const imgProps = getImageInitialProps(props.productDetails, 'back')

        if (carouselAPI?.selectedScrollSnap() !== 1)
          carouselAPI?.scrollTo(1)

        setImagesContext((prev) => {
          return {
            ...prev,
            backImages: [
              ...prev.backImages,
              {
                src: modalImage.src,
                ...imgProps
              },
            ],
          };
        });
      } else {
        toast({
          description: "You can add only 4 images.",
          variant: 'destructive',
          duration: 4000
        });
      }
    }
    if (window.innerWidth < 500)
      window.scrollTo(0, 0);
    setOpenModal(false);
  };

  const handlePromptImageClick = (src: string) => {
    const imgSrc = src

    if (selectedImages.includes(imgSrc)) {
      setImagesContext({
        ...imagesContext,
        frontImages: imagesContext.frontImages.filter(
          (image) => image.src != imgSrc,
        ),
        backImages: imagesContext.backImages.filter(
          (image) => image.src != imgSrc,
        ),
      });
    } else {
      setOpenModal(true);
      setModalImage({ src: imgSrc });
    }
  };

  const changeModalImage = (n: string) => {
    setModalImage({ src: n })
  }

  const isImageUsed = useMemo(() => {
    const ctxImages = [...imagesContext.frontImages, ...imagesContext.backImages]
    return ctxImages.some(x => x.src === modalImage.src)
  }, [imagesContext.frontImages.length, imagesContext.backImages.length, modalImage.src])

  return (
    <div className="col-span-1">
      <div className="flex items-center justify-between px-0 md:px-2">
        {imagesContext.frontImages.length + imagesContext.backImages.length > 0 ? <div>
          <h2 className="text-sm md:text-xl font-semibold dark:text-white">
            Design Here
          </h2>
          <p className="text-xs md:text-sm text-slate-500">
            {props.uploadedImage ? "Upload" : "Generate"} your designs here!
          </p>
        </div> : <div className="flex flex-col items-start sm:flex-row sm:items-end gap-0 md:gap-2">
          <h2 className="inline-flex items-center text-sm font-semibold text-slate-900  md:text-xl">
            What do you want to design?
          </h2>
          {!props.uploadedImage && <Link href={Properties.routes.EXPLORE_DESIGNS_WITH_TSHIRTS} className="text-xs leading-5 text-indigo-500 ">
            See Examples
          </Link>}
        </div>
        }

        {!props.uploadedImage && <InstructionModal />}
      </div>

      {!props.uploadedImage && <ImageGenerateItem handlePromptImageClick={handlePromptImageClick} onReset={onReset} selectedImages={selectedImages} productSlug={props.productDetails.slug} colorType={nonURIContext.colorMode} />}
      {props.uploadedImage && <ImageUploader selectedImages={selectedImages} handlePromptImageClick={handlePromptImageClick} />}
      <ImageViewModal currentImage={modalImage.src} open={openModal} setOpen={setOpenModal} setCurrentImage={changeModalImage} >
        <div className="flex w-full flex-wrap items-center justify-center md:flex-nowrap">
          <div className=" my-1 me-0 w-full  sm:w-auto" />

          {!isImageUsed && <div className="grid grid-cols-6 items-center justify-center gap-2">
            <Button
              onClick={handleAddToFrontImage}
              size={"sm"}
              className={cn(" mx-0 w-full col-span-2", {
                'col-span-6': !props.productDetails.boundingBoxBack,
                "col-span-2": props.downloadEnabled,
                'col-span-3': !props.downloadEnabled,
                "col-span-8": props.productDetails.colors.length === 0,
              })}
            >
              {props.productDetails.colors.length === 0 ? "Add Design" : "Add to Front Side"}
            </Button>

            {props.productDetails.boundingBoxBack && (
              <Button
                onClick={handleAddToBackImage}
                size={"sm"}
                className={cn("w-full", {
                  "col-span-2": props.downloadEnabled,
                  'col-span-3': !props.downloadEnabled
                })}
              >
                Add to Back Side
              </Button>
            )}

            {props.downloadEnabled && (
              <Button
                size={"sm"}
                className="col-span-2 mx-0 flex size-full items-center justify-center"
                asChild
              >
                <a href={modalImage.src} download={'out.png'} target="_blank">
                  <Download className="me-2 size-4" /> Download
                </a>
              </Button>
            )}
          </div>}
          {
            isImageUsed && <Button>
              <CheckCircle className="size-5 me-2" /> Already using this design!
            </Button>
          }
        </div>
      </ImageViewModal>
    </div>
  );
}
